import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Spin, Input, Pagination, Radio, Tooltip, Button as AntButton } from 'antd'
import { COLORS } from 'theme'
import { SearchOutlined, UserAddOutlined } from '@ant-design/icons'
import Button from 'components/Button'
import CheckIconWhite from 'components/icons/CheckIconWhite'
import VirtualizedCheckboxGroup from 'components/UIComponents/Filter'
import { StyledTabs } from '../../Style/style'
import { StateCityMessage } from 'modules/Systemconfig/Partials/TableSettings/Style/style'
import FilterIcon from '../../../../components/icons/FilterIcon'
import ArrowDropDown from '../../../../components/icons/ArrowDropDown'
import AssessmentProgressBar from 'components/AssessmentProgressBar'

import {
  MenuFlex,
  StyledTab,
  SubmitFlex,
} from 'modules/Dashboard/Partials/UsersFilter/Styles/style'
import FilterButtonComponent from 'components/FilterButton'
import { FilterContainer, FilterPopup } from 'components/FilterButton/styles'
import CollegeCard from 'components/CollegeCard'
import CheckboxGroup from 'components/CheckboxGroup'
import RadioButtonGroup from 'components/RadioButtonGroup'
import { Link } from 'react-router-dom'

import {
  SearchSection,
  ShowingText,
  Overlay,
  TableTop,
  CustomStyledTable,
  AssessmentName,
} from '../../Style/style'

// Format date in DD MMM, YYYY format
const formatDate = dateString => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  // Assessment start/end are stored as IST-wall-clock-as-UTC; render in UTC
  // (else IST browsers show +1 day past 18:30 UTC).
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

// Define assessment types for the filter
const assessmentTypeOptions = [
  { value: 'communication', label: 'Communication Assessment' },
  { value: 'behavior', label: 'Behavioral Assessment' },
  { value: 'cognitive', label: 'Cognitive Aptitude' },
  { value: 'tech_mcq', label: 'Technical Assessment - MCQ' },
  { value: 'tech_coding', label: 'Technical Assessment - Coding' },
  { value: 'Aptitude', label: 'Aptitude Assessment' },
  { value: 'Custom_Assessment', label: 'Custom Assessment' },
  { value: 'Role_Based', label: 'Role-Based Assessment' },
  { value: '', label: 'All Assessment Types' },
]

const ActiveAssessmentTable = ({
  activeAssessments = [],
  loading = false,
  filters = {},
  metrics,
  page = 0,
  limit = 10,
  totalItems = 0,
  onFilterChange = () => {},
  onPageChange = () => {},
  stateCityPairs = [],
  onAssessmentClick = () => {},
  entityType = 'college',
  onAddCandidate = () => {},
}) => {
  const [trialPeriod, setTrialPeriod] = useState(filters.isTrial || false)
  const [subscription, setSubscription] = useState(
    filters.isSubscribed || false
  )
  const [showFilters, setShowFilters] = useState(false)
  const [selectedAssessmentType, setSelectedAssessmentType] = useState(
    filters.type_name || ''
  )
  const [searchValue, setSearchValue] = useState(filters.searchBy || '')

  // Extract unique states and cities from state city pairs
  const { states, citiesByState } = useMemo(() => {
    const statesSet = new Set()
    const cityMap = new Map()

    stateCityPairs.forEach(([state, city]) => {
      statesSet.add(state)
      if (!cityMap.has(state)) {
        cityMap.set(state, new Set())
      }
      cityMap.get(state).add(city)
    })

    // Convert to array format needed by VirtualizedCheckboxGroup
    const statesList = Array.from(statesSet).map(state => ({
      state,
      stateId: state,
    }))

    // Convert city sets to arrays
    const citiesMap = {}
    cityMap.forEach((cities, state) => {
      citiesMap[state] = Array.from(cities).map(city => ({
        city,
        cityId: city,
      }))
    })

    return {
      states: statesList,
      citiesByState: citiesMap,
    }
  }, [stateCityPairs])

  const [stateId, setStateId] = useState([])
  const [cityId, setCityId] = useState([])
  const [stateList, setStateList] = useState(states)
  const [cityList, setCityList] = useState([])
  const [stateNames, setStateNames] = useState(filters.states || [])
  const [cityName, setCityName] = useState(filters.cities || [])
  const [degreeSearch, setDegreeSearch] = useState('')
  const [check, setCheck] = useState(false)

  // Memoize handlers to prevent unnecessary re-renders
  const handleFilterChange = useCallback(
    (key, value) => {
      const newFilters = {
        ...filters,
        ...(typeof key === 'object' ? key : { [key]: value }),
        updateFilters: true,
      }
      onFilterChange(newFilters)
    },
    [filters, onFilterChange]
  )

  // Simplify initial setup effect
  useEffect(() => {
    // if (!filters.type_name) {
    handleFilterChange('type_name', filters.type_name || '')
    // }
  }, [])

  // Update states from filters, but only when necessary
  useEffect(() => {
    if (!filters.fromSearch && !filters.fromCheckbox) {
      if (filters.type_name !== undefined) {
        setSelectedAssessmentType(filters.type_name)
      }
      if (filters.isTrial !== undefined) {
        setTrialPeriod(filters.isTrial)
      }
      if (filters.isSubscribed !== undefined) {
        setSubscription(filters.isSubscribed)
      }
      if (filters.searchBy !== undefined && !filters.updateFilters) {
        setSearchValue(filters.searchBy || '')
      }
      setStateNames(filters.states || [])
      setCityName(filters.cities || [])
    }
  }, [filters])

  // Update states and cities when assessment list changes
  useEffect(() => {
    setStateList(states)
  }, [states])

  // Update cities when state selection changes
  useEffect(() => {
    if (stateId?.length) {
      const availableCities = stateId.flatMap(
        state => citiesByState[state] || []
      )
      setCityList(availableCities)
    } else {
      setCityList([])
    }
  }, [stateId, citiesByState])

  // Reset city selection when state changes
  useEffect(() => {
    setCityId([])
    setCityName([])
  }, [stateId])

  // Add debounce for search
  const handleSearch = useCallback(
    e => {
      const value = e.target.value
      setSearchValue(value)

      handleFilterChange({
        searchBy: value,
        fromSearch: true,
        updateFilters: true,
      })
    },
    [handleFilterChange]
  )

  // Handler for assessment type change in the filter
  const handleAssessmentTypeChange = value => {
    setSelectedAssessmentType(value)
  }

  const submitFilter = useCallback(() => {
    setShowFilters(false)
    // Reset to first page when applying new filters
    const newFilters = {
      ...filters,
      type_name: selectedAssessmentType,
      states: stateNames || [],
      cities: cityName || [],
      pageNo: 0,
      updateFilters: true,
    }
    onFilterChange(newFilters)
  }, [filters, selectedAssessmentType, stateNames, cityName, onFilterChange])

  const handleTableChange = useCallback(
    (pagination, tableFilters, sorter) => {
      if (sorter) {
        handleFilterChange({
          sort: sorter.order === 'ascend' ? 'asc' : 'desc',
          order: sorter.columnKey || sorter.field,
        })
      }
    },
    [handleFilterChange]
  )

  const handlePageChange = useCallback(
    newPage => {
      onPageChange(newPage - 1, {
        ...filters,
        pageOnly: true,
      })
    },
    [filters, onPageChange]
  )

  // Assessment Type Filter component
  const AssessmentTypeFilter = () => (
    <div style={{ padding: '10px' }}>
      <Radio.Group
        value={selectedAssessmentType}
        onChange={e => handleAssessmentTypeChange(e.target.value)}
        style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
      >
        {assessmentTypeOptions.map(option => (
          <Radio key={option.value} value={option.value}>
            {option.label}
          </Radio>
        ))}
      </Radio.Group>
    </div>
  )

  // Memoize menu component
  const menu = useMemo(
    () => (
      <MenuFlex column>
        <StyledTab
          border={true}
          tabPosition={'left'}
          type="card"
          padding="15px"
          popupClassName="filter"
          items={[
            {
              label: 'Assessment Type',
              key: '1',
              children: <AssessmentTypeFilter />,
            },
            {
              label: 'State',
              key: '2',
              children: (
                <VirtualizedCheckboxGroup
                  options={states}
                  value={stateId}
                  setValue={setStateId}
                  valueParam="state"
                  labelParam="state"
                  setStateName={setStateNames}
                  filterId={true}
                  stateName={stateNames}
                  degreeSearch={degreeSearch}
                  setDegreeSearch={setDegreeSearch}
                  check={check}
                />
              ),
            },
            {
              label: 'City',
              key: '3',
              children: (
                <>
                  {stateId?.length ? (
                    <VirtualizedCheckboxGroup
                      options={cityList}
                      value={cityId}
                      setValue={setCityId}
                      valueParam="city"
                      labelParam="city"
                      filterId={true}
                      setStateName={setCityName}
                      stateName={cityName}
                      degreeSearch={degreeSearch}
                      setDegreeSearch={setDegreeSearch}
                      check={check}
                    />
                  ) : (
                    <StateCityMessage>
                      Please select state first
                    </StateCityMessage>
                  )}
                </>
              ),
            },
          ]}
        />
        <SubmitFlex right>
          <Button.Secondary
            text="Cancel"
            onClick={() => setShowFilters(false)}
          />
          <Button.Primary
            onClick={submitFilter}
            text="Apply Filter"
            icon={<CheckIconWhite />}
          />
        </SubmitFlex>
      </MenuFlex>
    ),
    [
      states,
      cityList,
      stateId,
      cityId,
      stateNames,
      cityName,
      degreeSearch,
      check,
      selectedAssessmentType, // Added this dependency
    ]
  )

  // Memoize columns configuration
  const columns = useMemo(
    () => [
      {
        title: 'ASSESSMENT TYPE',
        dataIndex: 'assessmentType',
        key: 'assessmentType',
        width: '25%',
        render: (text, record) => {
          // Map assessment type codes to readable names
          const typeMap = {
            communication: 'Communication Assessment',
            behavior: 'Behavioral Assessment',
            cognitive: 'Cognitive Aptitude',
            tech_mcq: 'Technical Assessment - MCQ',
            tech_coding: 'Technical Assessment - Coding',
            // One float sent as several assessments, listed as the one thing
            // it was sent as.
            Mix_Match: 'Mix & Match Assessment',
            Aptitude: 'Aptitude Assessment',
            Custom_Assessment: 'Custom Assessment',
            Role_Based: 'Role-Based Assessment',
          }
          return (
            <div>
              <div>{typeMap[text] || text || '-'}</div>
              {record.assessmentName && (
                <AssessmentName
                  onClick={e => {
                    e.stopPropagation()
                    onAssessmentClick(record)
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onAssessmentClick(record)
                    }
                  }}
                >
                  {record.assessmentName}
                </AssessmentName>
              )}
            </div>
          )
        },
      },
      {
        title: 'ASSMT. COMP VS SENT.',
        dataIndex: 'assessmentSent',
        key: 'assessmentSent',
        width: '20%',
        render: (sent, record) => (
          <AssessmentProgressBar
            sent={sent || 0}
            completed={record.assessmentCompleted || 0}
          />
        ),
      },
      {
        title: 'ENTITY NAME',
        dataIndex: 'collegeName',
        key: 'collegeName',
        width: '25%',
        render: (text, record) => (
          <CollegeCard
            name={record.collegeName}
            city={record.collegeCity}
            state=""
            logoUrl={record.collegeLogoUrl}
          />
        ),
      },
      {
        title: 'DEGREE & DEPARTMENT',
        dataIndex: 'degreeDepartment',
        key: 'degreeDepartment',
        width: '20%',
        render: (degrees, record) => (
          <div>
            {degrees && degrees.length > 0 ? (
              <>
                {degrees.map((degree, index) => (
                  <div key={index}>
                    {degree.short_form || degree.degree_name} -{' '}
                    {degree.stream_name}
                  </div>
                ))}
                {record.hasMoreDegreeDepartments && (
                  <div style={{ fontWeight: 'bold' }}>
                    + {record.degreeDepartmentCount - degrees.length} more
                  </div>
                )}
              </>
            ) : (
              '-'
            )}
          </div>
        ),
      },
      {
        title: 'ASSMT. DATES',
        dataIndex: 'assessmentSubDate',
        // key drives the sort param sent to the backend; it must match a field
        // the API recognizes ('startDate'), not the display dataIndex.
        key: 'startDate',
        width: '15%',
        sorter: true,
        defaultSortOrder: 'descend',
        render: (startDate, record) => (
          <div>
            {formatDate(startDate)}{' '}
            {record.endDate ? ` - ${formatDate(record.endDate)}` : ''}
          </div>
        ),
      },
      {
        title: 'ACTIONS',
        key: 'actions',
        width: '90px',
        align: 'center',
        render: (_, record) => (
          <Tooltip title="Add Candidate">
            <AntButton
              type="text"
              size="small"
              icon={<UserAddOutlined />}
              style={{ color: '#3249d7', borderRadius: 6, width: 32, height: 32 }}
              onClick={e => {
                e.stopPropagation()
                onAddCandidate({ ...record, entityType })
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [onAssessmentClick, onAddCandidate, entityType]
  )

  return (
    <>
      {/* <CheckboxGroup.Styled
        options={[
          {
            label: `Trial Period (${metrics?.trialCount || 0})`,
            value: 'trial',
          },
          {
            label: `Subscription (${metrics?.subscribedCount || 0})`,
            value: 'subscription',
          },
        ]}
        value={[
          ...(trialPeriod ? ['trial'] : []),
          ...(subscription ? ['subscription'] : []),
        ]}
        onChange={values => {
          const isTrial = values.includes('trial')
          const isSubscribed = values.includes('subscription')
          handleFilterChange({
            isTrial,
            isSubscribed,
          })
          setTrialPeriod(isTrial)
          setSubscription(isSubscribed)
        }}
      /> */}

      <TableTop>
        <SearchSection>
          <Input
            placeholder="Search by keyword"
            prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
            value={searchValue}
            onChange={handleSearch}
          />
        </SearchSection>

        <FilterContainer>
          <FilterButtonComponent onClick={() => setShowFilters(!showFilters)}>
            <FilterIcon />
            <span>Filter</span>
            <ArrowDropDown />
          </FilterButtonComponent>
          {showFilters && (
            <>
              <FilterPopup>{menu}</FilterPopup>
              <Overlay onClick={() => setShowFilters(false)} />
            </>
          )}
        </FilterContainer>

        <ShowingText>
          Showing {Math.min(activeAssessments.length + 10 * page)} of{' '}
          {totalItems}
        </ShowingText>
      </TableTop>

      <Spin spinning={loading}>
        <CustomStyledTable
          columns={columns}
          dataSource={activeAssessments}
          pagination={false}
          rowKey="id"
          onChange={handleTableChange}
        />
      </Spin>

      {totalItems > limit && (
        <div
          style={{
            margin: '16px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination
            current={parseInt(page) + 1}
            total={totalItems}
            pageSize={parseInt(limit)}
            onChange={handlePageChange}
          />
        </div>
      )}

    </>
  )
}

export default ActiveAssessmentTable
